import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Loader from "../../Generic/Loader";
import SearchBar from "../../Generic/SearchBar";
import { errorNotification } from "../../Generic/Notifications";
import StudioService from "../../CookBook/Services/StudioService";

const DISH_SEARCH_MIN_KEYWORD_LENGTH = 3;
const DISH_SEARCH_DEBOUNCE_MS = 400;

const SearchWrap = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const ResultsPanel = styled.div`
	position: absolute;
	top: calc(100% + 0.25rem);
	left: 0;
	right: 0;
	z-index: 2;
	max-height: 10rem;
	overflow-y: auto;
	background: #fff;
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 8px;
`;

const ResultsList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
`;

const ResultItem = styled.li`
	padding: 0.75rem 1rem;
	cursor: pointer;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	color: #36395a;

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: #f1f1f1;
	}
`;

const StatusText = styled.p`
	margin: 0;
	color: rgba(0, 0, 0, 0.55);
	font-size: 0.9rem;
	text-align: center;
	padding: 1rem 0;
`;

const LoaderWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem 0;
`;

const DishSearch = ({ selectedLabel = "", onSelect, onClear }) => {
	const [query, setQuery] = useState("");
	const [editing, setEditing] = useState(false);
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searched, setSearched] = useState(false);
	const requestIdRef = useRef(0);

	useEffect(() => {
		const trimmed = query.trim();

		if (!trimmed || trimmed.length < DISH_SEARCH_MIN_KEYWORD_LENGTH) {
			setResults([]);
			setLoading(false);
			setSearched(false);
			requestIdRef.current += 1;
			return undefined;
		}

		const timerId = setTimeout(async () => {
			const requestId = ++requestIdRef.current;
			setLoading(true);
			const { data, error } = await StudioService.searchDishesByTitle(trimmed);
			if (requestId !== requestIdRef.current) return;
			setLoading(false);
			setSearched(true);
			if (error) {
				errorNotification(error);
				setResults([]);
				return;
			}
			setResults(Array.isArray(data?.dishes) ? data.dishes : []);
		}, DISH_SEARCH_DEBOUNCE_MS);

		return () => {
			clearTimeout(timerId);
			requestIdRef.current += 1;
		};
	}, [query]);

	const trimmedQuery = query.trim();
	const belowMinLength =
		trimmedQuery.length > 0 && trimmedQuery.length < DISH_SEARCH_MIN_KEYWORD_LENGTH;
	const showPanel = editing && Boolean(trimmedQuery);
	const inputValue = editing ? query : selectedLabel;

	function handleChange(e) {
		const nextQuery = e.target.value;
		const trimmed = nextQuery.trim();
		setEditing(true);
		setQuery(trimmed);
		if (!trimmed) {
			if(onClear) onClear();
			setLoading(false);
			setSearched(false);
			setResults([]);
			return;
		}
		if (trimmed.length >= DISH_SEARCH_MIN_KEYWORD_LENGTH) {
			setLoading(true);
			setSearched(false);
			return;
		}
		setLoading(false);
		setSearched(false);
		setResults([]);
	}

	function handleSelect(dish) {
		if (!dish?._id) return;
		if(onSelect) 
			onSelect({value: dish._id, valueLabel: dish.title || dish._id});
		setQuery("");
		setEditing(false);
		setResults([]);
		setSearched(false);
		setLoading(false);
		requestIdRef.current += 1;
	}

	return (
		<SearchWrap>
			<SearchBar
				value={inputValue}
				onChange={handleChange}
				placeholder="Search dishes by title"
			/>
			{showPanel ? (
				<ResultsPanel>
					{loading ? (
						<LoaderWrap>
							<Loader />
						</LoaderWrap>
					) : belowMinLength ? (
						<StatusText>Type at least 3 characters</StatusText>
					) : searched && results.length === 0 ? (
						<StatusText>No dishes found</StatusText>
					) : (
						<ResultsList>
							{results.map((dish) => (
								<ResultItem key={dish._id} onClick={() => handleSelect(dish)}>
									{dish.title}
								</ResultItem>
							))}
						</ResultsList>
					)}
				</ResultsPanel>
			) : null}
		</SearchWrap>
	);
};

export default DishSearch;
